import type { GameMode } from '../../shared/modes';

/** Inputs to the shared post-match action policy; client readiness only, never authorization. */
export interface PostMatchContext {
  mode: GameMode;
  continuation: 'continue-room' | 'archive-only';
  asyncRoom: boolean;
  /** Room phase the recap belongs to; async recaps can outlive their round. */
  phase: 'results' | 'playing' | 'lobby' | 'countdown';
  isHost: boolean;
  /** Client estimate of the Live Versus reveal gate; the server still decides. */
  gated: boolean;
}

export interface PostMatchActions {
  primaryLabel: string | null;
  lobbyLabel: string | null;
  leaderboardLabel: string | null;
  status: string | null;
}

/**
 * Pure action policy for the shared post-match presentation. Leaving is never
 * gated; gated Live Versus continuation simply hides its primary until the
 * reveal ends. The server authorizes every command at execution.
 */
export function postMatchActions(context: PostMatchContext, hostName: string): PostMatchActions {
  const { mode, continuation, asyncRoom, phase, isHost, gated } = context;
  if (mode === 'competitive') {
    // A ranked room plays one round; the next match comes from the queue.
    return {
      primaryLabel: 'Queue Again',
      lobbyLabel: null,
      leaderboardLabel: 'Leaderboard',
      status: null,
    };
  }
  if (continuation === 'archive-only' || mode === 'daily') {
    return {
      primaryLabel: null,
      lobbyLabel: null,
      leaderboardLabel: 'Leaderboard',
      status: null,
    };
  }
  if (mode !== 'versus') {
    return {
      primaryLabel: 'Play Again',
      lobbyLabel: null,
      leaderboardLabel: null,
      status: null,
    };
  }
  if (asyncRoom && phase === 'playing') {
    return {
      primaryLabel: 'Continue',
      lobbyLabel: null,
      leaderboardLabel: null,
      status: null,
    };
  }
  if (asyncRoom) {
    // Completed Turns checkpoint: any member may open the next round.
    return {
      primaryLabel: 'Next Round',
      lobbyLabel: 'Turn Lobby',
      leaderboardLabel: null,
      status: null,
    };
  }
  // Neither a roster nor history may spoil the shared reveal before standings.
  if (gated) {
    return {
      primaryLabel: null,
      lobbyLabel: null,
      leaderboardLabel: null,
      status: 'Revealing the results…',
    };
  }
  // Live Versus: shared lobby dismissal is local; starting stays host-owned.
  if (isHost) {
    return {
      primaryLabel: 'Play Again',
      lobbyLabel: 'Back to the Lobby',
      leaderboardLabel: null,
      status: null,
    };
  }
  return {
    primaryLabel: null,
    lobbyLabel: 'Back to the Lobby',
    leaderboardLabel: null,
    status: `waiting for ${hostName || 'the host'} to play again`,
  };
}
